import { CLONE_VOICE_TYPE_LABEL, isTripleCloneVoice, VOICE_ENGINE } from '../constants/voicePresets'
import TRIPLE_ACCESS_PLATFORM_DEFAULTS, { FLAGSHIP_S1_MODEL } from '../constants/tripleAccessPlatform'

export const PRESET_MODE = {
  ENGINE: 'preset',
  CUSTOM: 'custom',
}

/** 服务版本：至尊版 / 旗舰版（声网）/ 标准版（G1、G2） */
export const SERVICE_VERSION = Object.freeze({
  SUPREME: 'supreme',
  FLAGSHIP: 'flagship',
  STANDARD: 'standard',
})

export function resolveServiceVersion({ serviceVersion, presetMode, tripleSelected, flagshipSelected } = {}) {
  const raw = String(serviceVersion != null ? serviceVersion : '').trim()
  if (
    raw === SERVICE_VERSION.SUPREME ||
    raw === SERVICE_VERSION.FLAGSHIP ||
    raw === SERVICE_VERSION.STANDARD
  ) {
    return raw
  }
  if (flagshipSelected === true || tripleSelected === SERVICE_VERSION.FLAGSHIP) {
    return SERVICE_VERSION.FLAGSHIP
  }
  return (presetMode || PRESET_MODE.ENGINE) === PRESET_MODE.CUSTOM
    ? SERVICE_VERSION.STANDARD
    : SERVICE_VERSION.SUPREME
}

const HUASHU_FEE_VERSIONS = new Set([
  SERVICE_VERSION.SUPREME,
  SERVICE_VERSION.FLAGSHIP,
  SERVICE_VERSION.STANDARD,
])

/** 系统费版本 id：21 至尊版，22 旗舰版，23 标准版。字段与 cls / resources 一致 */
const HUASHU_FEE_VERSION_BY_ID = Object.freeze({
  21: SERVICE_VERSION.SUPREME,
  22: SERVICE_VERSION.FLAGSHIP,
  23: SERVICE_VERSION.STANDARD,
})

function resolveHuashuFeeCls(item) {
  const cls = String(item.cls != null ? item.cls : '').trim()
  if (HUASHU_FEE_VERSIONS.has(cls)) return cls
  const id = Number(item.id)
  return HUASHU_FEE_VERSION_BY_ID[id] || ''
}

/** 话术编辑系统费接口里的可用版本（cls + resources） */
export function normalizeHuashuFeeVersions(data) {
  const list = Array.isArray(data) ? data : []
  const seen = new Set()
  const versions = []
  list.forEach((item) => {
    if (!item || typeof item !== 'object') return
    const cls = resolveHuashuFeeCls(item)
    if (!cls || seen.has(cls)) return
    seen.add(cls)
    const resources = Array.isArray(item.resources) ? item.resources : []
    versions.push({
      id: item.id,
      name: String(item.name != null ? item.name : '').trim(),
      cls,
      resources: resources
        .filter((row) => row && typeof row === 'object')
        .map((row) => ({
          id: String(row.id != null ? row.id : '').trim(),
          name: String(row.name != null ? row.name : '').trim(),
        }))
        .filter((row) => row.id || row.name),
    })
  })
  return versions
}

/** JSON 里能否判断出版本。没有 service_version / 各版 selected / preset 时返回空串 */
export function readJsonServiceVersion(voiceSettings) {
  const vs = voiceSettings && typeof voiceSettings === 'object' ? voiceSettings : {}
  const hasSignal = Boolean(
    (vs.service_version != null && String(vs.service_version).trim() !== '') ||
      (vs.flagship_access && typeof vs.flagship_access === 'object') ||
      (vs.triple_access && typeof vs.triple_access === 'object') ||
      (vs.system_preset && typeof vs.system_preset === 'object'),
  )
  if (!hasSignal) return ''
  return resolveServiceVersion({
    serviceVersion: vs.service_version,
    presetMode: resolveVoicePresetModeFromSettings({
      systemPreset: vs.system_preset,
      tripleAccess: vs.triple_access,
    }),
    tripleSelected: vs.triple_access && vs.triple_access.selected,
    flagshipSelected: vs.flagship_access && vs.flagship_access.selected,
  })
}

/**
 * 进入页面时的服务版本：
 * 接口有可用版本时，已保存版本在列表内则沿用，否则优先标准版，再取列表第一项；
 * 接口没有数据时用 JSON；两边都没有则默认标准版。
 */
export function resolveServiceVersionWithFee(feeVersions, voiceSettings) {
  const allowed = normalizeHuashuFeeVersions(feeVersions).map((item) => item.cls)
  const fromJson = readJsonServiceVersion(voiceSettings)
  if (allowed.length) {
    if (fromJson && allowed.indexOf(fromJson) !== -1) return fromJson
    if (allowed.indexOf(SERVICE_VERSION.STANDARD) !== -1) return SERVICE_VERSION.STANDARD
    return allowed[0]
  }
  return fromJson || SERVICE_VERSION.STANDARD
}

/** 用户自定义 TTS 资源 engine 前缀 */
export const CUSTOM_TTS_RESOURCE_PREFIX = 'custom_resource:'

/** TTS 资源供应商选项（添加资源弹窗） */
export const TTS_RESOURCE_PROVIDERS = TRIPLE_ACCESS_PLATFORM_DEFAULTS.tts.resourceProviders

/** 三段式 TTS 默认引擎（dictionary/tts 的 engineName） */
export const DEFAULT_TRIPLE_TTS_ENGINE = TRIPLE_ACCESS_PLATFORM_DEFAULTS.tts.defaultEngine

export function isCustomTtsResourceEngine(engine) {
  return String(engine != null ? engine : '').trim().startsWith(CUSTOM_TTS_RESOURCE_PREFIX)
}

export function buildCustomTtsResourceEngineValue(id) {
  const raw = String(id != null ? id : '').trim()
  return raw ? `${CUSTOM_TTS_RESOURCE_PREFIX}${raw}` : ''
}

export function parseCustomTtsResourceId(engine) {
  const raw = String(engine != null ? engine : '').trim()
  if (!isCustomTtsResourceEngine(raw)) return ''
  return raw.slice(CUSTOM_TTS_RESOURCE_PREFIX.length)
}

export function createCustomTtsResource(payload = {}) {
  const id = `cr_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  return {
    id,
    type: 'tts',
    name: String(payload.name != null ? payload.name : '').trim(),
    provider: String(payload.provider != null ? payload.provider : '').trim(),
    group_id: String(payload.group_id != null ? payload.group_id : '').trim(),
    key: String(payload.key != null ? payload.key : '').trim(),
    description: String(payload.description != null ? payload.description : '').trim(),
  }
}

export function sanitizeCustomTtsResources(list) {
  const src = Array.isArray(list) ? list : []
  return src
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const id = String(item.id != null ? item.id : '').trim()
      const name = String(item.name != null ? item.name : '').trim()
      if (!id || !name) return null
      return {
        id,
        type: 'tts',
        name,
        provider: String(item.provider != null ? item.provider : '').trim(),
        group_id: String(item.group_id != null ? item.group_id : '').trim(),
        key: String(item.key != null ? item.key : '').trim(),
        description: String(item.description != null ? item.description : '').trim(),
      }
    })
    .filter(Boolean)
}

export function buildCustomTtsResourceOptions(customResources = []) {
  return sanitizeCustomTtsResources(customResources).map((item) => ({
    value: buildCustomTtsResourceEngineValue(item.id),
    label: item.name,
    isCustom: true,
  }))
}

/** 用户自定义 ASR 资源 engine 前缀 */
export const CUSTOM_ASR_RESOURCE_PREFIX = 'custom_asr_resource:'

/** ASR 资源供应商选项（添加资源弹窗） */
export const ASR_RESOURCE_PROVIDERS = TRIPLE_ACCESS_PLATFORM_DEFAULTS.asr.resourceProviders

export function isCustomAsrResourceEngine(engine) {
  return String(engine != null ? engine : '').trim().startsWith(CUSTOM_ASR_RESOURCE_PREFIX)
}

export function buildCustomAsrResourceEngineValue(id) {
  const raw = String(id != null ? id : '').trim()
  return raw ? `${CUSTOM_ASR_RESOURCE_PREFIX}${raw}` : ''
}

export function parseCustomAsrResourceId(engine) {
  const raw = String(engine != null ? engine : '').trim()
  if (!isCustomAsrResourceEngine(raw)) return ''
  return raw.slice(CUSTOM_ASR_RESOURCE_PREFIX.length)
}

export function createCustomAsrResource(payload = {}) {
  const id = `ar_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  return {
    id,
    type: 'asr',
    name: String(payload.name != null ? payload.name : '').trim(),
    provider: String(payload.provider != null ? payload.provider : '').trim(),
    app_id: String(payload.app_id != null ? payload.app_id : '').trim(),
    secret: String(payload.secret != null ? payload.secret : '').trim(),
    key: String(payload.key != null ? payload.key : '').trim(),
    description: String(payload.description != null ? payload.description : '').trim(),
  }
}

export function sanitizeCustomAsrResources(list) {
  const src = Array.isArray(list) ? list : []
  return src
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const id = String(item.id != null ? item.id : '').trim()
      const name = String(item.name != null ? item.name : '').trim()
      if (!id || !name) return null
      return {
        id,
        type: 'asr',
        name,
        provider: String(item.provider != null ? item.provider : '').trim(),
        app_id: String(item.app_id != null ? item.app_id : '').trim(),
        secret: String(item.secret != null ? item.secret : '').trim(),
        key: String(item.key != null ? item.key : '').trim(),
        description: String(item.description != null ? item.description : '').trim(),
      }
    })
    .filter(Boolean)
}

export function buildCustomAsrResourceOptions(customResources = []) {
  return sanitizeCustomAsrResources(customResources).map((item) => ({
    value: buildCustomAsrResourceEngineValue(item.id),
    label: item.name,
    isCustom: true,
  }))
}

export function buildAsrEngineOptionsFromDictionaryList(data) {
  const list = Array.isArray(data) ? data : []
  return list
    .filter((item) => item && item.value)
    .map((item) => ({
      value: String(item.value),
      label: String(item.name != null && item.name !== '' ? item.name : item.value),
    }))
}

export function resolveDefaultTripleAsrEngineFromOptions(engineOptions = []) {
  const list = Array.isArray(engineOptions) ? engineOptions : []
  return list[0] ? list[0].value : ''
}

export function syncTripleAsrEngineWithDictionary(asr, engineOptions = []) {
  if (!asr || typeof asr !== 'object') return
  const current = String(asr.engine != null ? asr.engine : '').trim()
  if (isCustomAsrResourceEngine(current)) return
  const list = Array.isArray(engineOptions) ? engineOptions : []
  const customOptions = buildCustomAsrResourceOptions(asr.custom_resources)
  const values = new Set([
    ...list.map((item) => item.value),
    ...customOptions.map((item) => item.value),
  ])
  if (current && values.has(current)) return
  if (!list.length && !customOptions.length) {
    asr.engine = ''
    return
  }
  asr.engine = resolveDefaultTripleAsrEngineFromOptions(list)
}

export const CUSTOM_LLM_MODEL_PREFIX = 'custom_model:'

/** LLM 模型供应商选项（添加模型弹窗） */
export const LLM_RESOURCE_PROVIDERS = TRIPLE_ACCESS_PLATFORM_DEFAULTS.llm.resourceProviders

/** 平台预设配置（唯一配置源，见 constants/tripleAccessPlatform.js） */
export { TRIPLE_ACCESS_PLATFORM_DEFAULTS }

/** 系统 LLM 资源 value 前缀（resource 字段：`system:{modelId}`） */
export const SYSTEM_LLM_RESOURCE_PREFIX = TRIPLE_ACCESS_PLATFORM_DEFAULTS.llm.systemResourcePrefix

/** 系统预设 LLM 模型目录（由平台配置派生） */
export const LLM_SYSTEM_MODEL_CATALOG = TRIPLE_ACCESS_PLATFORM_DEFAULTS.llm.systemModelCatalog

/** 默认系统 LLM 的 model id（对应 catalog.id） */
export const DEFAULT_SYSTEM_LLM_MODEL_ID = TRIPLE_ACCESS_PLATFORM_DEFAULTS.llm.defaultSystemModelId

export function buildSystemLlmResourceValue(modelId) {
  const id = String(modelId != null ? modelId : '').trim()
  return id ? `${SYSTEM_LLM_RESOURCE_PREFIX}${id}` : ''
}

export function parseSystemLlmModelIdFromResource(resource) {
  const raw = String(resource != null ? resource : '').trim()
  if (!raw.startsWith(SYSTEM_LLM_RESOURCE_PREFIX)) return ''
  return raw.slice(SYSTEM_LLM_RESOURCE_PREFIX.length)
}

function findSystemLlmCatalogEntry(modelId) {
  const id = String(modelId != null ? modelId : '').trim()
  if (!id) return null
  return LLM_SYSTEM_MODEL_CATALOG.find((item) => item.id === id) || null
}

function resolveCatalogResourceLabel(item) {
  const resourceLabel = String(item && item.resourceLabel != null ? item.resourceLabel : '').trim()
  if (resourceLabel) return resourceLabel
  return String(item && item.label != null ? item.label : '').trim()
}

function resolveCatalogModelLabel(item) {
  const modelLabel = String(item && item.modelLabel != null ? item.modelLabel : '').trim()
  if (modelLabel) return modelLabel
  return String(item && item.id != null ? item.id : '').trim()
}

function mapCatalogItemToSystemLlmResource(item) {
  return {
    value: buildSystemLlmResourceValue(item.id),
    label: resolveCatalogResourceLabel(item),
    model: item.id,
    modelLabel: resolveCatalogModelLabel(item),
  }
}

/** 字典 / 目录里的产品线。G2 只看 G2，其它场景只看空 product_line */
export const PRODUCT_LINE_G2 = 'G2'

export const FLAGSHIP_MODEL_SELECTION_PREFIX = 'custom:'

export function normalizeProductLine(raw) {
  return String(raw != null ? raw : '').trim()
}

export function getDictionaryItemProductLine(item) {
  if (!item || typeof item !== 'object') return ''
  if (item.product_line != null) return String(item.product_line).trim()
  if (item.productLine != null) return String(item.productLine).trim()
  return ''
}

export function matchesProductLine(itemOrLine, expected) {
  const actual =
    itemOrLine && typeof itemOrLine === 'object'
      ? getDictionaryItemProductLine(itemOrLine)
      : normalizeProductLine(itemOrLine)
  const want = normalizeProductLine(expected)
  if (want === PRODUCT_LINE_G2) return actual === PRODUCT_LINE_G2
  return actual === ''
}

export function filterDictionaryByProductLine(list, productLine) {
  return (Array.isArray(list) ? list : []).filter((item) => matchesProductLine(item, productLine))
}

export function getCatalogProductLine(modelId) {
  const entry = findSystemLlmCatalogEntry(modelId)
  return entry ? normalizeProductLine(entry.productLine) : ''
}

export function resolveTripleAccessProductLine(llm) {
  if (!llm || typeof llm !== 'object') return ''
  return getCatalogProductLine(resolveFlagshipModelIdFromLlm(llm))
}

export function isG2ProductLine(productLine) {
  return normalizeProductLine(productLine) === PRODUCT_LINE_G2
}

function getDictionaryItemMatchFields(item) {
  if (!item || typeof item !== 'object') return []
  return [item.value, item.name, item.engineName, item.label, item.code]
    .map((field) => String(field != null ? field : '').trim())
    .filter(Boolean)
}

function matchesAllowedDictionaryKeys(item, allowedKeys) {
  const keys = (Array.isArray(allowedKeys) ? allowedKeys : [])
    .map((key) => String(key != null ? key : '').trim())
    .filter(Boolean)
  if (!keys.length) return true
  const fields = getDictionaryItemMatchFields(item)
  return keys.some((key) => fields.some((field) => field === key))
}

/** G1 系统 TTS 资源只保留平台白名单；G2 仍按产品线展示。发音人继续用字典 ttsVocal */
export function filterG1TtsSystemEngines(list, productLine) {
  return filterG1SystemDictionary(list, productLine, TRIPLE_ACCESS_PLATFORM_DEFAULTS.tts.g1AllowedEngines)
}

/** G1 系统 ASR 只保留「字节2.0」；G2 仍按产品线展示 */
export function filterG1AsrSystemEngines(list, productLine) {
  return filterG1SystemDictionary(list, productLine, TRIPLE_ACCESS_PLATFORM_DEFAULTS.asr.g1AllowedEngines)
}

function filterG1SystemDictionary(list, productLine, allowedKeys) {
  const byLine = filterDictionaryByProductLine(list, productLine)
  if (isG2ProductLine(productLine)) return byLine
  return byLine.filter((item) => matchesAllowedDictionaryKeys(item, allowedKeys))
}

/** 当前是否为三段式 G2（级联语音大模型-G2） */
export function isG2TripleAccessMode(voiceCall) {
  if (!voiceCall || typeof voiceCall !== 'object') return false
  if ((voiceCall.presetMode || PRESET_MODE.ENGINE) !== PRESET_MODE.CUSTOM) return false
  return isG2ProductLine(
    resolveTripleAccessProductLine(voiceCall.tripleAccess && voiceCall.tripleAccess.llm),
  )
}

export function resolveFlagshipModelIdFromLlm(llm) {
  if (!llm || typeof llm !== 'object') return ''
  const fromResource = parseSystemLlmModelIdFromResource(llm.resource)
  return fromResource || String(llm.model != null ? llm.model : '').trim()
}

export const DEFAULT_COPY_VOICE_PATH = '/api/task/scene/llm/copyvoice'

export function resolveCopyVoicePathFromLlm(llm) {
  const entry =
    findSystemLlmCatalogEntry(resolveFlagshipModelIdFromLlm(llm)) || getDefaultSystemLlmCatalogEntry()
  const path = String(entry && entry.copyVoicePath != null ? entry.copyVoicePath : '').trim()
  return path || DEFAULT_COPY_VOICE_PATH
}

export function resolveFlagshipCatalogEntryFromLlm(llm) {
  return findSystemLlmCatalogEntry(resolveFlagshipModelIdFromLlm(llm)) || getDefaultSystemLlmCatalogEntry()
}

export function buildSystemLlmResourcesByProductLine(productLine) {
  return LLM_SYSTEM_MODEL_CATALOG.filter((item) => matchesProductLine(item.productLine, productLine)).map(
    mapCatalogItemToSystemLlmResource,
  )
}

/** 顶部「大模型」下拉的展示名，写入 llmsettings.voice_settings.model_name */
export function resolveSelectedLlmModelName({ presetMode, voiceEngine, llm, serviceVersion } = {}) {
  const version = resolveServiceVersion({ serviceVersion, presetMode })
  if (version === SERVICE_VERSION.FLAGSHIP) {
    return FLAGSHIP_S1_MODEL.label
  }
  if (version === SERVICE_VERSION.STANDARD) {
    const entry = resolveFlagshipCatalogEntryFromLlm(llm)
    return entry && entry.label ? String(entry.label).trim() : ''
  }
  const engine = String(voiceEngine || VOICE_ENGINE.ALIYUN).trim()
  if (engine === VOICE_ENGINE.QWEN_OMNI) {
    return CLONE_VOICE_TYPE_LABEL[VOICE_ENGINE.QWEN_OMNI] || ''
  }
  return CLONE_VOICE_TYPE_LABEL[VOICE_ENGINE.ALIYUN] || ''
}

export function buildFlagshipModelSelectOptions(includeModelId = '') {
  const keepId = String(includeModelId != null ? includeModelId : '').trim()
  return LLM_SYSTEM_MODEL_CATALOG.filter((item) => !item.hidden || item.id === keepId).map((item) => ({
    value: `${FLAGSHIP_MODEL_SELECTION_PREFIX}${item.id}`,
    label: item.label,
    modelId: item.id,
  }))
}

export function parseFlagshipModelSelectionKey(key) {
  const raw = String(key != null ? key : '').trim()
  if (!raw.startsWith(FLAGSHIP_MODEL_SELECTION_PREFIX)) return ''
  return raw.slice(FLAGSHIP_MODEL_SELECTION_PREFIX.length)
}

export function applySystemLlmCatalogToTripleAccess(llm, modelId) {
  if (!llm || typeof llm !== 'object') return
  const entry = findSystemLlmCatalogEntry(modelId) || getDefaultSystemLlmCatalogEntry()
  if (!entry) return
  llm.resource = buildSystemLlmResourceValue(entry.id)
  llm.model = entry.id
  syncLlmFieldsFromResource(llm, buildSystemLlmResourcesByProductLine(entry.productLine))
}

export function getDefaultSystemLlmCatalogEntry() {
  return (
    findSystemLlmCatalogEntry(DEFAULT_SYSTEM_LLM_MODEL_ID) || LLM_SYSTEM_MODEL_CATALOG[0] || null
  )
}

/** 默认系统 LLM 资源 value（triple_access.llm.resource） */
export const DEFAULT_SYSTEM_LLM_RESOURCE_VALUE = buildSystemLlmResourceValue(
  DEFAULT_SYSTEM_LLM_MODEL_ID,
)

/** 系统预设 LLM 资源（平台提供，由 catalog 派生） */
export const SYSTEM_LLM_RESOURCES = Object.freeze(
  LLM_SYSTEM_MODEL_CATALOG.map(mapCatalogItemToSystemLlmResource),
)

const LLM_PROVIDER_DEFAULT_MODEL = TRIPLE_ACCESS_PLATFORM_DEFAULTS.llm.providerDefaultModelId

export function isCustomLlmModelResource(resource) {
  return String(resource != null ? resource : '').trim().startsWith(CUSTOM_LLM_MODEL_PREFIX)
}

export function buildCustomLlmModelResourceValue(id) {
  const raw = String(id != null ? id : '').trim()
  return raw ? `${CUSTOM_LLM_MODEL_PREFIX}${raw}` : ''
}

export function parseCustomLlmModelId(resource) {
  const raw = String(resource != null ? resource : '').trim()
  if (!isCustomLlmModelResource(raw)) return ''
  return raw.slice(CUSTOM_LLM_MODEL_PREFIX.length)
}

export function resolveDefaultModelForLlmProvider(provider) {
  const key = String(provider != null ? provider : '').trim()
  return key && LLM_PROVIDER_DEFAULT_MODEL[key] ? LLM_PROVIDER_DEFAULT_MODEL[key] : ''
}

export function createCustomLlmModel(payload = {}) {
  const id = `cm_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
  const provider = String(payload.provider != null ? payload.provider : '').trim()
  const model = String(payload.model != null ? payload.model : '').trim()
    || resolveDefaultModelForLlmProvider(provider)
  return {
    id,
    type: 'llm',
    name: String(payload.name != null ? payload.name : '').trim(),
    provider,
    url: String(payload.url != null ? payload.url : '').trim(),
    api_key: String(payload.api_key != null ? payload.api_key : '').trim(),
    description: String(payload.description != null ? payload.description : '').trim(),
    model,
  }
}

export function sanitizeCustomLlmModels(list) {
  const src = Array.isArray(list) ? list : []
  return src
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const id = String(item.id != null ? item.id : '').trim()
      const name = String(item.name != null ? item.name : '').trim()
      if (!id || !name) return null
      const provider = String(item.provider != null ? item.provider : '').trim()
      const model = String(item.model != null ? item.model : '').trim()
        || resolveDefaultModelForLlmProvider(provider)
      return {
        id,
        type: 'llm',
        name,
        provider,
        url: String(item.url != null ? item.url : '').trim(),
        api_key: String(item.api_key != null ? item.api_key : '').trim(),
        description: String(item.description != null ? item.description : '').trim(),
        model,
      }
    })
    .filter(Boolean)
}

export function buildCustomLlmModelOptions(customModels = []) {
  return sanitizeCustomLlmModels(customModels).map((item) => ({
    value: buildCustomLlmModelResourceValue(item.id),
    label: item.name,
    isCustom: true,
  }))
}

export function getLlmResourceConfig(
  resource,
  customModels = [],
  systemResources = SYSTEM_LLM_RESOURCES,
) {
  const raw = String(resource != null ? resource : '').trim()
  if (!raw) return null
  if (isCustomLlmModelResource(raw)) {
    const id = parseCustomLlmModelId(raw)
    const item = sanitizeCustomLlmModels(customModels).find((row) => row.id === id)
    if (!item) return null
    return {
      model: item.model,
      api_key: item.api_key,
      base_url: item.url,
    }
  }
  const system = (Array.isArray(systemResources) ? systemResources : SYSTEM_LLM_RESOURCES)
    .find((item) => item.value === raw)
  if (system) {
    return {
      model: String(system.model != null ? system.model : '').trim(),
      api_key: String(system.api_key != null ? system.api_key : '').trim(),
      base_url: String(system.base_url != null ? system.base_url : '').trim(),
    }
  }
  return null
}

export function syncLlmFieldsFromResource(
  llm,
  systemResources = SYSTEM_LLM_RESOURCES,
) {
  if (!llm || typeof llm !== 'object') return
  const config = getLlmResourceConfig(llm.resource, llm.custom_models, systemResources)
  if (!config) return
  llm.model = config.model
  llm.api_key = config.api_key
  llm.base_url = config.base_url
}

export function resolveDefaultLlmResource(
  llm,
  systemResources = SYSTEM_LLM_RESOURCES,
) {
  const src = llm && typeof llm === 'object' ? llm : {}
  const current = String(src.resource != null ? src.resource : '').trim()
  if (current) return current

  const customModels = sanitizeCustomLlmModels(src.custom_models)
  const model = String(src.model != null ? src.model : '').trim()
  if (model) {
    const systemHit = (Array.isArray(systemResources) ? systemResources : SYSTEM_LLM_RESOURCES)
      .find((item) => String(item.model != null ? item.model : '').trim() === model)
    if (systemHit) return systemHit.value
    const customHit = customModels.find((item) => item.model === model || item.name === model)
    if (customHit) return buildCustomLlmModelResourceValue(customHit.id)
  }

  const list = Array.isArray(systemResources) ? systemResources : SYSTEM_LLM_RESOURCES
  return list[0] ? list[0].value : ''
}

export function resolveDefaultTripleLlmResourceFromOptions(
  resourceOptions = [],
  systemResources = SYSTEM_LLM_RESOURCES,
) {
  const values = new Set(
    (Array.isArray(resourceOptions) ? resourceOptions : []).map((item) => item.value),
  )
  const preferred = (Array.isArray(systemResources) ? systemResources : SYSTEM_LLM_RESOURCES)
    .find((item) => item.value === DEFAULT_SYSTEM_LLM_RESOURCE_VALUE)
  if (preferred && values.has(preferred.value)) return preferred.value
  const first = (Array.isArray(resourceOptions) ? resourceOptions : [])[0]
  return first ? first.value : ''
}

/** 历史数据中的引擎 id → dictionary engineName */
const LEGACY_TRIPLE_TTS_ENGINE_MAP = Object.freeze({
  ali: 'aliyun',
  ali_lite: 'sambert-aliyun',
})

/**
 * dictionary/tts 列表 → engineName → ttsVocal[]
 * 与 Tts.vue 一致：发音人按当前 engineName 直接取 ttsVocal，不做额外过滤
 */
export function buildTtsMapFromDictionaryList(data) {
  const list = Array.isArray(data) ? data : []
  return list.reduce((map, next) => {
    if (!next || !next.engineName) return map
    map[next.engineName] = Array.isArray(next.ttsVocal) ? next.ttsVocal : []
    return map
  }, {})
}

/** dictionary/tts 全量列表 → 引擎下拉选项（与 Tts.vue 的 ttsList 一致） */
export function buildTtsEngineOptionsFromDictionaryList(data) {
  const list = Array.isArray(data) ? data : []
  return list
    .filter((item) => item && item.engineName)
    .map((item) => ({
      value: String(item.engineName),
      label: String(item.label != null && item.label !== '' ? item.label : item.engineName),
    }))
}

/** 从字典列表解析默认引擎（优先 cosyvoice3.5-aliyun，否则取列表首项） */
export function resolveDefaultTripleTtsEngineFromOptions(engineOptions = []) {
  const list = Array.isArray(engineOptions) ? engineOptions : []
  const preferred = list.find((item) => item.value === DEFAULT_TRIPLE_TTS_ENGINE)
  if (preferred) return preferred.value
  return list[0] ? list[0].value : DEFAULT_TRIPLE_TTS_ENGINE
}

/**
 * 将 triple_access.tts.engine 规范为 dictionary engineName
 * @param {string} engine
 * @param {Record<string, unknown[]>} ttsMap
 * @param {{ value: string }[]} [engineOptions]
 */
export function normalizeTripleTtsEngine(engine, ttsMap = {}, engineOptions = []) {
  const raw = String(engine != null ? engine : '').trim()
  if (!raw) return DEFAULT_TRIPLE_TTS_ENGINE
  if (isCustomTtsResourceEngine(raw)) return raw

  const optionValues = new Set(
    (Array.isArray(engineOptions) ? engineOptions : []).map((item) => item.value),
  )
  if (Object.prototype.hasOwnProperty.call(ttsMap, raw) || optionValues.has(raw)) return raw

  const legacy = LEGACY_TRIPLE_TTS_ENGINE_MAP[raw]
  if (legacy) {
    if (Object.prototype.hasOwnProperty.call(ttsMap, legacy) || optionValues.has(legacy)) return legacy
    if (raw === 'ali_lite') {
      const hit = Object.keys(ttsMap).find((key) => key.includes('sambert') || key.includes('omni'))
      if (hit) return hit
    }
    if (raw === 'ali') {
      const hit = Object.keys(ttsMap).find((key) => key === 'aliyun' || key.endsWith('-aliyun'))
      if (hit) return hit
    }
  }

  return raw
}

/**
 * 三段式 TTS 发音人：dictionary/tts 当前引擎发音人（与 Tts.vue speakers 一致），再追加克隆音色
 */
export function buildTripleTtsVoiceOptions({
  ttsEngine,
  ttsMap,
  clonedVoices,
  engineOptions = [],
  extraSpeakers = [],
}) {
  const engine = normalizeTripleTtsEngine(ttsEngine, ttsMap, engineOptions)
  const presetList = Array.isArray(ttsMap[engine]) ? ttsMap[engine] : []
  const presetOptions = presetList
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const value = String(item.vocalName != null ? item.vocalName : '').trim()
      const label = String(item.label != null ? item.label : value).trim() || value
      return value ? { value, label } : null
    })
    .filter(Boolean)

  const customOptions = (Array.isArray(extraSpeakers) ? extraSpeakers : [])
    .map((item) => {
      const value = String(
        item && item.vocalName != null ? item.vocalName : item != null ? item : '',
      ).trim()
      return value ? { value, label: value } : null
    })
    .filter(Boolean)

  const cloneOptions = (Array.isArray(clonedVoices) ? clonedVoices : [])
    .filter(isTripleCloneVoice)
    .map((row) => {
      const value = getTripleCloneVoiceId(row)
      const label = String(row.name != null ? row.name : '').trim() || value
      return value ? { value, label } : null
    })
    .filter(Boolean)

  const seen = new Set()
  const merged = []
  ;[...presetOptions, ...customOptions, ...cloneOptions].forEach((item) => {
    if (seen.has(item.value)) return
    seen.add(item.value)
    merged.push(item)
  })
  return merged
}

/** 将三段式 TTS 发音人选项拆分为「系统预设」与「我的音色」 */
export function splitTripleTtsVoiceOptions(allOptions, clonedVoices = []) {
  const cloneIds = new Set(
    (Array.isArray(clonedVoices) ? clonedVoices : [])
      .filter(isTripleCloneVoice)
      .map((row) => getTripleCloneVoiceId(row))
      .filter(Boolean),
  )
  const system = []
  const mine = []
  ;(Array.isArray(allOptions) ? allOptions : []).forEach((item) => {
    if (!item || !item.value) return
    if (cloneIds.has(item.value)) {
      mine.push(item)
    } else {
      system.push(item)
    }
  })
  return { system, mine }
}

export function createDefaultTripleAccessLlm() {
  const entry = getDefaultSystemLlmCatalogEntry()
  return {
    config_id: 0,
    resource: entry ? buildSystemLlmResourceValue(entry.id) : '',
    model: entry ? entry.id : '',
    api_key: '',
    base_url: '',
    custom_models: [],
  }
}

export function createDefaultTripleAccessAsr() {
  return {
    config_id: 0,
    engine: '',
    custom_resources: [],
  }
}

export function createDefaultTripleAccessTts() {
  return {
    config_id: 0,
    engine: DEFAULT_TRIPLE_TTS_ENGINE,
    voice_id: '',
    custom_resources: [],
  }
}

/** 三段式克隆音色在 TTS 中应使用的 voice_id */
export function getTripleCloneVoiceId(row) {
  if (!row || !isTripleCloneVoice(row)) return ''
  return String(row.voice_id != null ? row.voice_id : '').trim()
}

/** 在当前引擎的字典发音人列表中，将 label / vocalName 规范为 vocalName */
export function resolveDictionaryTtsVocalName(value, ttsMap = {}, ttsEngine = '', engineOptions = []) {
  const raw = String(value != null ? value : '').trim()
  if (!raw) return ''
  const engine = normalizeTripleTtsEngine(ttsEngine, ttsMap, engineOptions)
  const presetList = Array.isArray(ttsMap[engine]) ? ttsMap[engine] : []
  const byVocalName = presetList.find(
    (item) => item && String(item.vocalName != null ? item.vocalName : '').trim() === raw,
  )
  if (byVocalName) return raw
  const byLabel = presetList.find(
    (item) => item && String(item.label != null ? item.label : '').trim() === raw,
  )
  if (byLabel) {
    return String(byLabel.vocalName != null ? byLabel.vocalName : '').trim()
  }
  return ''
}

/**
 * 将表单中的发音人值规范为 voice_id：
 * - 字典混合音：使用 /dictionary/tts 的 vocalName（如 Xiaoyun）
 * - 三段式克隆音色：使用克隆列表的 voice_id；若误传 name 则映射为 voice_id
 */
export function resolveTripleTtsVoiceId(value, options = {}) {
  const opts = Array.isArray(options) ? { clonedVoices: options } : options
  const {
    clonedVoices = [],
    ttsMap = {},
    ttsEngine = '',
    engineOptions = [],
  } = opts
  const raw = String(value != null ? value : '').trim()
  if (!raw) return ''

  const dictionaryVocalName = resolveDictionaryTtsVocalName(
    raw,
    ttsMap,
    ttsEngine,
    engineOptions,
  )
  if (dictionaryVocalName) return dictionaryVocalName

  const clones = (Array.isArray(clonedVoices) ? clonedVoices : []).filter(isTripleCloneVoice)
  if (clones.some((row) => getTripleCloneVoiceId(row) === raw)) return raw
  const byCloneName = clones.find((row) => String(row.name != null ? row.name : '').trim() === raw)
  if (byCloneName) return getTripleCloneVoiceId(byCloneName) || raw
  return raw
}

export function createDefaultTripleAccess() {
  return {
    llm: createDefaultTripleAccessLlm(),
    asr: createDefaultTripleAccessAsr(),
    tts: createDefaultTripleAccessTts(),
  }
}

/** 旗舰版顶部下拉只有一项：对话式 AI 大模型-S1 */
export function buildFlagshipS1SelectOption() {
  return {
    value: `${FLAGSHIP_MODEL_SELECTION_PREFIX}${FLAGSHIP_S1_MODEL.id}`,
    label: FLAGSHIP_S1_MODEL.label,
    modelId: FLAGSHIP_S1_MODEL.id,
  }
}

/** 旗舰版接入配置里的 LLM 资源（声网 S1，接口后续再接） */
export function buildFlagshipS1SystemResources() {
  return [
    {
      value: buildSystemLlmResourceValue(FLAGSHIP_S1_MODEL.id),
      label: FLAGSHIP_S1_MODEL.resourceLabel,
      model: FLAGSHIP_S1_MODEL.id,
      modelLabel: FLAGSHIP_S1_MODEL.modelLabel,
    },
  ]
}

export function normalizeAgoraConfigId(value) {
  const id = Number(value)
  return Number.isFinite(id) && id > 0 ? Math.floor(id) : 0
}

function readAgoraConfigId(block, fallbackField) {
  const direct = normalizeAgoraConfigId(block && block.config_id)
  if (direct) return direct
  const raw = block && block[fallbackField]
  const text = String(raw != null ? raw : '').trim()
  if (!/^\d+$/.test(text)) return 0
  return normalizeAgoraConfigId(text)
}

export function createDefaultFlagshipAccess() {
  return {
    llm: {
      config_id: 0,
      resource: '',
      model: '',
      api_key: '',
      base_url: '',
      custom_models: [],
    },
    asr: {
      ...createDefaultTripleAccessAsr(),
      config_id: 0,
    },
    tts: {
      ...createDefaultTripleAccessTts(),
      config_id: 0,
      engine: '',
    },
  }
}

export function applyFlagshipS1ToAccess(access) {
  if (!access || typeof access !== 'object') return
  if (!access.llm || typeof access.llm !== 'object') return
  if (access.llm.config_id == null) access.llm.config_id = 0
}

/**
 * 根据 system_preset / triple_access 的 selected 恢复界面模式（兼容无 selected 的历史数据）
 */
export function resolveVoicePresetModeFromSettings({ systemPreset, tripleAccess } = {}) {
  const tripleSelected = !!(tripleAccess && tripleAccess.selected === true)
  const systemSelected = !!(systemPreset && systemPreset.selected === true)
  if (tripleSelected && !systemSelected) return PRESET_MODE.CUSTOM
  if (systemSelected && !tripleSelected) return PRESET_MODE.ENGINE
  if (systemPreset && systemPreset.mode === PRESET_MODE.CUSTOM) return PRESET_MODE.CUSTOM
  return PRESET_MODE.ENGINE
}

/** 仅持久化标准版界面可见字段。selected 为是否启用标准版。 */
export function buildTripleAccessSnapshot(tripleAccess, options = {}) {
  const { selected = false, clonedVoices = [] } = options
  const src = tripleAccess && typeof tripleAccess === 'object' ? tripleAccess : createDefaultTripleAccess()
  const llm = src.llm && typeof src.llm === 'object' ? src.llm : createDefaultTripleAccessLlm()
  const asr = src.asr && typeof src.asr === 'object' ? src.asr : createDefaultTripleAccessAsr()
  const tts = src.tts && typeof src.tts === 'object' ? src.tts : createDefaultTripleAccessTts()
  const rawVoiceId = tts.voice_id != null ? tts.voice_id : tts.voice
  const customResources = sanitizeCustomTtsResources(tts.custom_resources)
  const customAsrResources = sanitizeCustomAsrResources(asr.custom_resources)
  const customModels = sanitizeCustomLlmModels(llm.custom_models)
  const llmResource = String(
    llm.resource != null ? llm.resource : resolveDefaultLlmResource(llm),
  ).trim()
  const llmConfig = getLlmResourceConfig(llmResource, customModels) || {
    model: String(llm.model != null ? llm.model : '').trim(),
    api_key: String(llm.api_key != null ? llm.api_key : '').trim(),
    base_url: String(llm.base_url != null ? llm.base_url : '').trim(),
  }
  return {
    selected: !!selected,
    llm: {
      resource: llmResource,
      model: String(llmConfig.model != null ? llmConfig.model : '').trim(),
      api_key: String(llmConfig.api_key != null ? llmConfig.api_key : '').trim(),
      base_url: String(llmConfig.base_url != null ? llmConfig.base_url : '').trim(),
      custom_models: customModels,
    },
    asr: {
      engine: String(asr.engine != null ? asr.engine : '').trim(),
      custom_resources: customAsrResources,
    },
    tts: {
      engine: String(tts.engine != null ? tts.engine : DEFAULT_TRIPLE_TTS_ENGINE).trim()
        || DEFAULT_TRIPLE_TTS_ENGINE,
      voice_id: resolveTripleTtsVoiceId(rawVoiceId, {
        clonedVoices,
        ttsEngine: tts.engine,
      }),
      custom_resources: customResources,
    },
  }
}

/** 旗舰版单独落在 flagship_access。selected 为是否启用旗舰版，资源 id 写在 config_id，未选为 0。 */
export function buildFlagshipAccessSnapshot(access, options = {}) {
  const { selected = false } = options
  const ids = flagshipConfigIds(access)
  return {
    selected: !!selected,
    llm: { config_id: ids.llm },
    asr: { config_id: ids.asr },
    tts: { config_id: ids.tts },
  }
}

export function applyTripleAccessToForm(raw, target, options = {}) {
  const { clonedVoices = [] } = options
  const base = createDefaultTripleAccess()
  const src = raw && typeof raw === 'object' ? raw : {}
  const llmSrc = src.llm && typeof src.llm === 'object' ? src.llm : {}
  const asrSrc = src.asr && typeof src.asr === 'object' ? src.asr : {}
  const ttsSrc = src.tts && typeof src.tts === 'object' ? src.tts : {}
  const rawVoiceId = ttsSrc.voice_id != null ? ttsSrc.voice_id : ttsSrc.voice

  target.llm = {
    ...base.llm,
    config_id: normalizeAgoraConfigId(llmSrc.config_id),
    resource: llmSrc.resource != null
      ? String(llmSrc.resource)
      : resolveDefaultLlmResource(llmSrc),
    model: llmSrc.model != null ? String(llmSrc.model) : '',
    api_key: llmSrc.api_key != null ? String(llmSrc.api_key) : '',
    base_url: llmSrc.base_url != null ? String(llmSrc.base_url) : '',
    custom_models: sanitizeCustomLlmModels(llmSrc.custom_models),
  }
  syncLlmFieldsFromResource(target.llm)
  target.asr = {
    ...base.asr,
    config_id: normalizeAgoraConfigId(asrSrc.config_id),
    engine: asrSrc.engine != null ? String(asrSrc.engine) : '',
    custom_resources: sanitizeCustomAsrResources(asrSrc.custom_resources),
  }
  target.tts = {
    ...base.tts,
    config_id: normalizeAgoraConfigId(ttsSrc.config_id),
    engine: ttsSrc.engine != null ? String(ttsSrc.engine) : DEFAULT_TRIPLE_TTS_ENGINE,
    voice_id: resolveTripleTtsVoiceId(rawVoiceId, {
      clonedVoices,
      ttsEngine: ttsSrc.engine,
    }),
    custom_resources: sanitizeCustomTtsResources(ttsSrc.custom_resources),
  }
}

function flagshipConfigIds(access) {
  const src = access && typeof access === 'object' ? access : {}
  return {
    llm: readAgoraConfigId(src.llm, 'resource'),
    asr: readAgoraConfigId(src.asr, 'engine'),
    tts: readAgoraConfigId(src.tts, 'engine'),
  }
}

/** 从 flagship_access 恢复旗舰版资源 id；曾写在 triple_access.config_id 上的数据作为补救 */
export function restoreFlagshipAccess(tripleAccess, flagshipAccess, target) {
  if (!target || typeof target !== 'object') return
  const fromFlagship = flagshipConfigIds(flagshipAccess)
  const fromTriple = flagshipConfigIds(tripleAccess)
  const ids = fromFlagship.llm || fromFlagship.asr || fromFlagship.tts ? fromFlagship : fromTriple
  if (target.llm) target.llm.config_id = ids.llm
  if (target.asr) target.asr.config_id = ids.asr
  if (target.tts) target.tts.config_id = ids.tts
}
